import { useState, useEffect } from 'react';
import { apiService } from '../services/api';

interface PublicEvent {
  EVENT_ID?: number;
  id?: number;
  TITLE?: string;
  title?: string;
  DESCRIPTION?: string;
  description?: string;
  LOCATION?: string;
  location?: string;
  ADDRESS?: string;
  address?: string;
  LATITUDE?: number;
  latitude?: number;
  LONGITUDE?: number;
  longitude?: number;
  START_DATE?: string;
  startDate?: string;
  END_DATE?: string;
  endDate?: string;
  EXPECTED_ATTENDANCE?: number;
  expectedAttendance?: number;
  IS_VERIFIED?: boolean;
  isVerified?: boolean;
  IS_FEATURED?: boolean;
  isFeatured?: boolean;
  IMAGE_URL?: string;
  imageUrl?: string;
  IMAGES?: string[];
  images?: string[];
  thumbnailBase64?: string;
  mimeType?: string;
  CREATED_AT?: string;
  createdAt?: string;
}

interface UsePublicEventsReturn {
  events: PublicEvent[];
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

export const usePublicEvents = (params?: {
  page?: number;
  limit?: number;
  search?: string;
  startDate?: string;
  endDate?: string;
  category?: string;
  isFeatured?: boolean;
  sortBy?: string;
  sortOrder?: 'ASC' | 'DESC';
  enabled?: boolean;
}): UsePublicEventsReturn => {
  const [events, setEvents] = useState<PublicEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchEvents = async (showLoader: boolean = true) => {
    // Don't fetch if disabled
    if (params?.enabled === false) {
      setLoading(false);
      return;
    }

    try {
      if (showLoader) {
        setLoading(true);
      }
      setError(null);
      const response = await apiService.getPublicEvents(params);
      
      // Handle both array response and object with data property
      const eventsData = Array.isArray(response) ? response : response.data || (response as any).events || [];
      setEvents(eventsData);
    } catch (err) {
      console.error('Failed to fetch public events:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch events');
    } finally {
      if (showLoader) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    // Don't fetch if disabled
    if (params?.enabled === false) {
      return;
    }
    
    // Don't show loading for search-only changes if we already have data
    const isSearchOnlyChange = params?.search !== undefined && events.length > 0;
    fetchEvents(!isSearchOnlyChange);
  }, [params?.page, params?.limit, params?.search, params?.startDate, params?.endDate, params?.category, params?.isFeatured, params?.sortBy, params?.sortOrder, params?.enabled]);

  const refetch = () => {
    fetchEvents();
  };

  return {
    events,
    loading,
    error,
    refetch,
  };
};